import { Locales } from '@griply/types';

// More permissive pattern for words inside properties like goals, tags, and people.
// It allows almost any character, including numbers and colons at the start.
export const propertyWordPart =
  '[\\p{L}\\p{N}\\p{Extended_Pictographic}_&.+-:@!#]+'; // Allow '#' in property names.

// Define different "greedy" levels for parsing property names.
// A goal is the most greedy: it can contain tags and assignees. It only stops for a new goal or a terminator.
export const subsequentCharsForGoal = '(?:(?!\\s~|\u200B).)*';
// A tag can contain an assignee, but stops for a new goal or tag.
export const subsequentCharsForTag = '(?:(?!\\s[#~]|\u200B).)*';
// A person is the least greedy and stops for any new property.
export const subsequentCharsForPerson = '(?:(?!\\s[#@~]|\u200B).)*';

export const goalSubPattern = `${propertyWordPart}${subsequentCharsForGoal}`;
export const tagSubPattern = `${propertyWordPart}${subsequentCharsForTag}`;
export const personSubPattern = `${propertyWordPart}${subsequentCharsForPerson}`;

export const goalRegex =
  /(~[a-zA-Z-][a-zA-Z0-9-]*(?: [a-zA-Z-][a-zA-Z0-9-]*)*)/g;
export const tagPattern = '#[a-zA-Z-][a-zA-Z0-9-]*(?: [a-zA-Z-][a-zA-Z0-9-]*)*';
export const personPattern =
  '@[a-zA-Z-][a-zA-Z0-9-]*(?: [a-zA-Z-][a-zA-Z0-9-]*)*';
export const timePattern = `(?:\\d{1,2}:\\d{2}(?:am|pm)?|\\d{1,2}(?:am|pm))`;
export const timeRangePatternString = `\\b(${timePattern})\\s*-\\s*(${timePattern})\\b`;
export const singleTimePatternString = `(?<!!)\\b(${timePattern})(?!\\s*-)`;
export const durationPattern = '(?:\\d+h(?:\\s*\\d+m)?|\\d+m)';
// Reminder offsets additionally carry a day unit ("!2d before"); durations do not.
export const reminderOffsetPattern = `(?:\\d+d(?:\\s*\\d+h)?(?:\\s*\\d+m)?|${durationPattern})`;

export const pillSeparator = '\u00A0';
export const whitespace = '\u200B';

// The preposition introducing a clock time ("at 9:00"), as a regex fragment.
export const atKeywords: Record<Locales, string> = {
  [Locales.EnUS]: 'at',
  [Locales.NlNL]: 'om',
  [Locales.EsES]: 'a l(?:a|as)',
  [Locales.DeDE]: 'um',
  [Locales.PlPL]: 'o',
  [Locales.DaDK]: '(?:kl\\.?|klokken)',
  [Locales.SvSE]: '(?:kl\\.?|klockan)',
  [Locales.NbNO]: '(?:kl\\.?|klokken)',
  [Locales.ItIT]: '(?:alle|ore)',
  [Locales.FrFR]: '(?:à|a)',
  [Locales.PtPT]: '(?:às|as)',
  [Locales.PtBR]: '(?:às|as)',
};

// Portuguese date grammar is identical in both variants; only UI wording differs, so the
// vocabulary is defined once and spread into pt-PT and pt-BR.
const portugueseMonths: Record<string, number> = {
  jan: 0,
  janeiro: 0,
  fev: 1,
  fevereiro: 1,
  mar: 2,
  março: 2,
  marco: 2,
  abr: 3,
  abril: 3,
  mai: 4,
  maio: 4,
  jun: 5,
  junho: 5,
  jul: 6,
  julho: 6,
  ago: 7,
  agosto: 7,
  set: 8,
  setembro: 8,
  out: 9,
  outubro: 9,
  nov: 10,
  novembro: 10,
  dez: 11,
  dezembro: 11,
};

const portugueseDays: Record<string, string> = {
  seg: 'MO',
  segunda: 'MO',
  'segunda-feira': 'MO',
  ter: 'TU',
  terça: 'TU',
  terca: 'TU',
  'terça-feira': 'TU',
  'terca-feira': 'TU',
  qua: 'WE',
  quarta: 'WE',
  'quarta-feira': 'WE',
  qui: 'TH',
  quinta: 'TH',
  'quinta-feira': 'TH',
  sex: 'FR',
  sexta: 'FR',
  'sexta-feira': 'FR',
  sáb: 'SA',
  sab: 'SA',
  sábado: 'SA',
  sabado: 'SA',
  dom: 'SU',
  domingo: 'SU',
};

const portugueseRruleKeywords: Record<string, string> = {
  every: '(?:cada|todos os|todas as|todo|toda)',
  every_simple: 'cada',
  on: '(?:no|na|em)',
  on_simple: 'na',
  the: '(?:o|a|os|as)',
  the_simple: '',
  in: 'de',
  in_simple: 'de',
  of: 'de',
  and: 'e',
  days: 'dias',
  weeks: 'semanas',
  months: 'meses',
  years: 'anos',
  day: 'dia',
  week: 'semana',
  month: 'mês',
  year: 'ano',
  weekday: '(?:dias úteis|dia útil|dias uteis|dia util)',
  weekday_simple: 'dia útil',
  weekend: '(?:fins de semana|fim de semana)',
  weekend_simple: 'fim de semana',
  daily: 'diariamente',
  weekly: 'semanalmente',
  monthly: 'mensalmente',
  yearly: 'anualmente',
  ordinals: '(?:º|ª)',
  first: '(?:primeiro|primeira)',
  first_simple: 'primeiro',
  second: '(?:segundo|segunda)',
  second_simple: 'segundo',
  third: '(?:terceiro|terceira)',
  third_simple: 'terceiro',
  fourth: '(?:quarto|quarta)',
  fourth_simple: 'quarto',
  fifth: '(?:quinto|quinta)',
  fifth_simple: 'quinto',
  last: '(?:último|última|ultimo|ultima)',
  last_simple: 'último',
};

export const MONTHS: Record<Locales, Record<string, number>> = {
  [Locales.EnUS]: {
    jan: 0,
    january: 0,
    feb: 1,
    february: 1,
    mar: 2,
    march: 2,
    apr: 3,
    april: 3,
    may: 4,
    jun: 5,
    june: 5,
    jul: 6,
    july: 6,
    aug: 7,
    august: 7,
    sep: 8,
    september: 8,
    oct: 9,
    october: 9,
    nov: 10,
    november: 10,
    dec: 11,
    december: 11,
  },
  [Locales.NlNL]: {
    jan: 0,
    januari: 0,
    feb: 1,
    februari: 1,
    mrt: 2,
    maart: 2,
    apr: 3,
    april: 3,
    mei: 4,
    jun: 5,
    juni: 5,
    jul: 6,
    juli: 6,
    aug: 7,
    augustus: 7,
    sep: 8,
    september: 8,
    okt: 9,
    oktober: 9,
    nov: 10,
    november: 10,
    dec: 11,
    december: 11,
  },
  [Locales.EsES]: {
    ene: 0,
    enero: 0,
    feb: 1,
    febrero: 1,
    mar: 2,
    marzo: 2,
    abr: 3,
    abril: 3,
    may: 4,
    mayo: 4,
    jun: 5,
    junio: 5,
    jul: 6,
    julio: 6,
    ago: 7,
    agosto: 7,
    sep: 8,
    septiembre: 8,
    oct: 9,
    octubre: 9,
    nov: 10,
    noviembre: 10,
    dic: 11,
    diciembre: 11,
  },
  [Locales.DeDE]: {
    jan: 0,
    januar: 0,
    feb: 1,
    februar: 1,
    mär: 2,
    märz: 2,
    apr: 3,
    april: 3,
    mai: 4,
    jun: 5,
    juni: 5,
    jul: 6,
    juli: 6,
    aug: 7,
    august: 7,
    sep: 8,
    september: 8,
    okt: 9,
    oktober: 9,
    nov: 10,
    november: 10,
    dez: 11,
    dezember: 11,
  },
  // Polish dates use the genitive ("27 stycznia"), so both cases are keys.
  [Locales.PlPL]: {
    sty: 0,
    styczeń: 0,
    styczen: 0,
    stycznia: 0,
    lut: 1,
    luty: 1,
    lutego: 1,
    mar: 2,
    marzec: 2,
    marca: 2,
    kwi: 3,
    kwiecień: 3,
    kwiecien: 3,
    kwietnia: 3,
    maj: 4,
    maja: 4,
    cze: 5,
    czerwiec: 5,
    czerwca: 5,
    lip: 6,
    lipiec: 6,
    lipca: 6,
    sie: 7,
    sierpień: 7,
    sierpien: 7,
    sierpnia: 7,
    wrz: 8,
    wrzesień: 8,
    wrzesien: 8,
    września: 8,
    wrzesnia: 8,
    paź: 9,
    paz: 9,
    październik: 9,
    pazdziernik: 9,
    października: 9,
    pazdziernika: 9,
    lis: 10,
    listopad: 10,
    listopada: 10,
    gru: 11,
    grudzień: 11,
    grudzien: 11,
    grudnia: 11,
  },
  [Locales.DaDK]: {
    jan: 0,
    januar: 0,
    feb: 1,
    februar: 1,
    mar: 2,
    marts: 2,
    apr: 3,
    april: 3,
    maj: 4,
    jun: 5,
    juni: 5,
    jul: 6,
    juli: 6,
    aug: 7,
    august: 7,
    sep: 8,
    september: 8,
    okt: 9,
    oktober: 9,
    nov: 10,
    november: 10,
    dec: 11,
    december: 11,
  },
  [Locales.SvSE]: {
    jan: 0,
    januari: 0,
    feb: 1,
    februari: 1,
    mar: 2,
    mars: 2,
    apr: 3,
    april: 3,
    maj: 4,
    jun: 5,
    juni: 5,
    jul: 6,
    juli: 6,
    aug: 7,
    augusti: 7,
    sep: 8,
    september: 8,
    okt: 9,
    oktober: 9,
    nov: 10,
    november: 10,
    dec: 11,
    december: 11,
  },
  [Locales.NbNO]: {
    jan: 0,
    januar: 0,
    feb: 1,
    februar: 1,
    mar: 2,
    mars: 2,
    apr: 3,
    april: 3,
    mai: 4,
    jun: 5,
    juni: 5,
    jul: 6,
    juli: 6,
    aug: 7,
    august: 7,
    sep: 8,
    september: 8,
    okt: 9,
    oktober: 9,
    nov: 10,
    november: 10,
    des: 11,
    desember: 11,
  },
  [Locales.ItIT]: {
    gen: 0,
    gennaio: 0,
    feb: 1,
    febbraio: 1,
    mar: 2,
    marzo: 2,
    apr: 3,
    aprile: 3,
    mag: 4,
    maggio: 4,
    giu: 5,
    giugno: 5,
    lug: 6,
    luglio: 6,
    ago: 7,
    agosto: 7,
    set: 8,
    settembre: 8,
    ott: 9,
    ottobre: 9,
    nov: 10,
    novembre: 10,
    dic: 11,
    dicembre: 11,
  },
  [Locales.FrFR]: {
    janv: 0,
    jan: 0,
    janvier: 0,
    févr: 1,
    fevr: 1,
    fév: 1,
    fev: 1,
    février: 1,
    fevrier: 1,
    mars: 2,
    mar: 2,
    avr: 3,
    avril: 3,
    mai: 4,
    juin: 5,
    juil: 6,
    juillet: 6,
    août: 7,
    aout: 7,
    sept: 8,
    sep: 8,
    septembre: 8,
    oct: 9,
    octobre: 9,
    nov: 10,
    novembre: 10,
    déc: 11,
    dec: 11,
    décembre: 11,
    decembre: 11,
  },
  [Locales.PtPT]: portugueseMonths,
  [Locales.PtBR]: portugueseMonths,
};

// Maps day names to the RRULE standard two-letter abbreviation (e.g., MO, TU, WE).
export const dayMap: Record<Locales, Record<string, string>> = {
  [Locales.EnUS]: {
    mon: 'MO',
    monday: 'MO',
    tue: 'TU',
    tuesday: 'TU',
    wed: 'WE',
    wednesday: 'WE',
    thu: 'TH',
    thursday: 'TH',
    fri: 'FR',
    friday: 'FR',
    sat: 'SA',
    saturday: 'SA',
    sun: 'SU',
    sunday: 'SU',
  },
  [Locales.NlNL]: {
    ma: 'MO',
    maandag: 'MO',
    di: 'TU',
    dinsdag: 'TU',
    wo: 'WE',
    woensdag: 'WE',
    do: 'TH',
    donderdag: 'TH',
    vr: 'FR',
    vrijdag: 'FR',
    za: 'SA',
    zaterdag: 'SA',
    zo: 'SU',
    zondag: 'SU',
  },
  [Locales.EsES]: {
    lun: 'MO',
    lunes: 'MO',
    mar: 'TU',
    martes: 'TU',
    mié: 'WE',
    miércoles: 'WE',
    jue: 'TH',
    jueves: 'TH',
    vie: 'FR',
    viernes: 'FR',
    sáb: 'SA',
    sábado: 'SA',
    sábados: 'SA',
    dom: 'SU',
    domingo: 'SU',
    domingos: 'SU',
  },
  [Locales.DeDE]: {
    mo: 'MO',
    montag: 'MO',
    di: 'TU',
    dienstag: 'TU',
    mi: 'WE',
    mittwoch: 'WE',
    do: 'TH',
    donnerstag: 'TH',
    fr: 'FR',
    freitag: 'FR',
    sa: 'SA',
    samstag: 'SA',
    so: 'SU',
    sonntag: 'SU',
  },
  // Accusative forms are listed too ("w środę"); Sunday never keys on bare "nie" — it is Polish for "not".
  [Locales.PlPL]: {
    pon: 'MO',
    pn: 'MO',
    poniedziałek: 'MO',
    poniedzialek: 'MO',
    poniedziałki: 'MO',
    poniedzialki: 'MO',
    wt: 'TU',
    wtorek: 'TU',
    wtorki: 'TU',
    śr: 'WE',
    sr: 'WE',
    środa: 'WE',
    sroda: 'WE',
    środę: 'WE',
    srode: 'WE',
    środy: 'WE',
    srody: 'WE',
    czw: 'TH',
    czwartek: 'TH',
    czwartki: 'TH',
    pt: 'FR',
    piątek: 'FR',
    piatek: 'FR',
    piątki: 'FR',
    piatki: 'FR',
    sob: 'SA',
    sobota: 'SA',
    sobotę: 'SA',
    sobote: 'SA',
    soboty: 'SA',
    nd: 'SU',
    ndz: 'SU',
    niedz: 'SU',
    niedziela: 'SU',
    niedzielę: 'SU',
    niedziele: 'SU',
  },
  [Locales.DaDK]: {
    man: 'MO',
    mandag: 'MO',
    tir: 'TU',
    tirsdag: 'TU',
    ons: 'WE',
    onsdag: 'WE',
    tor: 'TH',
    torsdag: 'TH',
    fre: 'FR',
    fredag: 'FR',
    lør: 'SA',
    lørdag: 'SA',
    lordag: 'SA',
    søn: 'SU',
    søndag: 'SU',
    sondag: 'SU',
  },
  [Locales.SvSE]: {
    mån: 'MO',
    måndag: 'MO',
    mandag: 'MO',
    tis: 'TU',
    tisdag: 'TU',
    ons: 'WE',
    onsdag: 'WE',
    tor: 'TH',
    torsdag: 'TH',
    fre: 'FR',
    fredag: 'FR',
    lör: 'SA',
    lördag: 'SA',
    lordag: 'SA',
    sön: 'SU',
    söndag: 'SU',
    sondag: 'SU',
  },
  [Locales.NbNO]: {
    man: 'MO',
    mandag: 'MO',
    tir: 'TU',
    tirsdag: 'TU',
    ons: 'WE',
    onsdag: 'WE',
    tor: 'TH',
    torsdag: 'TH',
    fre: 'FR',
    fredag: 'FR',
    lør: 'SA',
    lørdag: 'SA',
    lordag: 'SA',
    søn: 'SU',
    søndag: 'SU',
    sondag: 'SU',
  },
  [Locales.ItIT]: {
    lun: 'MO',
    lunedì: 'MO',
    lunedi: 'MO',
    mar: 'TU',
    martedì: 'TU',
    martedi: 'TU',
    mer: 'WE',
    mercoledì: 'WE',
    mercoledi: 'WE',
    gio: 'TH',
    giovedì: 'TH',
    giovedi: 'TH',
    ven: 'FR',
    venerdì: 'FR',
    venerdi: 'FR',
    sab: 'SA',
    sabato: 'SA',
    dom: 'SU',
    domenica: 'SU',
  },
  [Locales.FrFR]: {
    lun: 'MO',
    lundi: 'MO',
    mar: 'TU',
    mardi: 'TU',
    mer: 'WE',
    mercredi: 'WE',
    jeu: 'TH',
    jeudi: 'TH',
    ven: 'FR',
    vendredi: 'FR',
    sam: 'SA',
    samedi: 'SA',
    dim: 'SU',
    dimanche: 'SU',
  },
  [Locales.PtPT]: portugueseDays,
  [Locales.PtBR]: portugueseDays,
};

// Keywords for parsing recurrence rules (RRULEs).
export const rruleKeywords: Record<Locales, Record<string, string>> = {
  [Locales.EnUS]: {
    every: 'every',
    every_simple: 'every',
    on: 'on',
    on_simple: 'on',
    the: 'the',
    the_simple: 'the',
    in: 'in',
    in_simple: 'in',
    days: 'days',
    weeks: 'weeks',
    months: 'months',
    years: 'years',
    day: 'day',
    week: 'week',
    month: 'month',
    year: 'year',
    weekday: 'weekday',
    weekday_simple: 'weekday',
    weekend: 'weekend',
    weekend_simple: 'weekend',
    daily: 'daily',
    weekly: 'weekly',
    monthly: 'monthly',
    yearly: 'yearly',
    ordinals: '(?:st|nd|rd|th)',
    first: 'first',
    first_simple: 'first',
    second: 'second',
    second_simple: 'second',
    third: 'third',
    third_simple: 'third',
    fourth: 'fourth',
    fourth_simple: 'fourth',
    fifth: 'fifth',
    fifth_simple: 'fifth',
    last: 'last',
    last_simple: 'last',
  },
  [Locales.NlNL]: {
    every: '(?:elk|elke)',
    every_simple: 'elke',
    on: 'op',
    on_simple: 'op',
    the: 'de',
    the_simple: 'de',
    in: 'in',
    in_simple: 'in',
    days: 'dagen',
    weeks: 'weken',
    months: 'maanden',
    years: 'jaar',
    day: 'dag',
    week: 'week',
    month: 'maand',
    year: 'jaar',
    weekday: 'weekdag',
    weekday_simple: 'weekdag',
    weekend: 'weekend',
    weekend_simple: 'weekend',
    daily: 'dagelijks',
    weekly: 'wekelijks',
    monthly: 'maandelijks',
    yearly: 'jaarlijks',
    ordinals: '(?:e|ste)',
    first: 'eerste',
    first_simple: 'eerste',
    second: 'tweede',
    second_simple: 'tweede',
    third: 'derde',
    third_simple: 'derde',
    fourth: 'vierde',
    fourth_simple: 'vierde',
    fifth: 'vijfde',
    fifth_simple: 'vijfde',
    last: 'laatste',
    last_simple: 'laatste',
  },
  [Locales.EsES]: {
    every: '(?:cada|todos los|todas las)',
    every_simple: 'cada',
    on: '(?:el|los)',
    on_simple: 'el',
    the: '(?:el|la|los|las)',
    // Rendered rules already carry "el" via on_simple, so no article is added.
    the_simple: '',
    in: '(?:en|de)',
    in_simple: 'en',
    // The genitive particle in dates ("27 de enero"); optional when parsing.
    of: 'de',
    days: 'días',
    weeks: 'semanas',
    months: 'meses',
    years: 'años',
    day: 'día',
    week: 'semana',
    month: 'mes',
    year: 'año',
    weekday: 'd[íi]a(?:s)? de (?:la )?semana',
    weekday_simple: 'día de la semana',
    weekend: 'fin(?:es)? de semana',
    weekend_simple: 'fin de semana',
    daily: 'diariamente',
    weekly: 'semanalmente',
    monthly: 'mensualmente',
    yearly: 'anualmente',
    ordinals: '(?:º|ª|er)',
    first: '(?:primer|primero)',
    first_simple: 'primero',
    second: 'segundo',
    second_simple: 'segundo',
    third: '(?:tercer|tercero)',
    third_simple: 'tercero',
    fourth: 'cuarto',
    fourth_simple: 'cuarto',
    fifth: 'quinto',
    fifth_simple: 'quinto',
    last: 'último',
    last_simple: 'último',
  },
  [Locales.DeDE]: {
    every: '(?:jeden|jede|jedes|alle)',
    every_simple: 'jeden',
    on: 'am',
    on_simple: 'am',
    the: '(?:den|dem|der)',
    the_simple: '',
    in: 'im',
    in_simple: 'im',
    and: 'und',
    days: 'tage',
    weeks: 'wochen',
    months: 'monate',
    years: 'jahre',
    day: 'tag',
    week: 'woche',
    month: 'monat',
    year: 'jahr',
    weekday: 'wochentag',
    weekday_simple: 'wochentag',
    weekend: 'wochenende',
    weekend_simple: 'wochenende',
    daily: 'täglich',
    weekly: 'wöchentlich',
    monthly: 'monatlich',
    yearly: 'jährlich',
    ordinals: '(?:\\.|te|ter|tes)',
    first: 'ersten',
    first_simple: 'ersten',
    second: 'zweiten',
    second_simple: 'zweiten',
    third: 'dritten',
    third_simple: 'dritten',
    fourth: 'vierten',
    fourth_simple: 'vierten',
    fifth: 'fünften',
    fifth_simple: 'fünften',
    last: 'letzten',
    last_simple: 'letzten',
  },
  [Locales.PlPL]: {
    every: '(?:co|w każdy|w każdą|w każde|każdego)',
    every_simple: 'co',
    on: 'w',
    on_simple: 'w',
    the: '',
    the_simple: '',
    in: 'w',
    in_simple: 'w',
    and: 'i',
    days: '(?:dni|dzień|dnia)',
    weeks: '(?:tygodnie|tygodni|tydzień)',
    months: '(?:miesiące|miesięcy|miesiąc)',
    years: '(?:lata|lat|rok)',
    day: 'dzień',
    week: 'tydzień',
    month: 'miesiąc',
    year: 'rok',
    weekday: '(?:dzień roboczy|dni robocze|dzien roboczy)',
    weekday_simple: 'dzień roboczy',
    weekend: '(?:weekend|weekendu)',
    weekend_simple: 'weekend',
    daily: 'codziennie',
    weekly: 'co tydzień',
    monthly: 'co miesiąc',
    yearly: 'co rok',
    ordinals: '(?:\\.)',
    first: '(?:pierwszego|pierwszą|pierwsza|pierwszy)',
    first_simple: 'pierwszy',
    second: '(?:drugiego|drugą|druga|drugi)',
    second_simple: 'drugi',
    third: '(?:trzeciego|trzecią|trzecia|trzeci)',
    third_simple: 'trzeci',
    fourth: '(?:czwartego|czwartą|czwarta|czwarty)',
    fourth_simple: 'czwarty',
    fifth: '(?:piątego|piątą|piąta|piąty)',
    fifth_simple: 'piąty',
    last: '(?:ostatniego|ostatnią|ostatnia|ostatni)',
    last_simple: 'ostatni',
  },
  [Locales.DaDK]: {
    every: '(?:hver|hvert)',
    every_simple: 'hver',
    on: 'den',
    on_simple: 'den',
    the: 'den',
    the_simple: '',
    in: 'i',
    in_simple: 'i',
    and: 'og',
    days: 'dage',
    weeks: 'uger',
    months: 'måneder',
    years: 'år',
    day: 'dag',
    week: 'uge',
    month: 'måned',
    year: 'år',
    weekday: '(?:hverdage|hverdag)',
    weekday_simple: 'hverdag',
    weekend: 'weekend',
    weekend_simple: 'weekend',
    daily: 'dagligt',
    weekly: 'ugentligt',
    monthly: 'månedligt',
    yearly: 'årligt',
    ordinals: '(?:\\.)',
    first: 'første',
    first_simple: 'første',
    second: 'anden',
    second_simple: 'anden',
    third: 'tredje',
    third_simple: 'tredje',
    fourth: 'fjerde',
    fourth_simple: 'fjerde',
    fifth: 'femte',
    fifth_simple: 'femte',
    last: 'sidste',
    last_simple: 'sidste',
  },
  [Locales.SvSE]: {
    every: '(?:varje|var)',
    every_simple: 'varje',
    on: 'den',
    on_simple: 'den',
    the: 'den',
    the_simple: '',
    in: 'i',
    in_simple: 'i',
    and: 'och',
    days: 'dagar',
    weeks: 'veckor',
    months: 'månader',
    years: 'år',
    day: 'dag',
    week: 'vecka',
    month: 'månad',
    year: 'år',
    weekday: '(?:vardagar|vardag)',
    weekday_simple: 'vardag',
    weekend: 'helg',
    weekend_simple: 'helg',
    daily: 'dagligen',
    weekly: 'veckovis',
    monthly: 'månadsvis',
    yearly: 'årligen',
    ordinals: '(?::a|:e|\\.)',
    first: 'första',
    first_simple: 'första',
    second: 'andra',
    second_simple: 'andra',
    third: 'tredje',
    third_simple: 'tredje',
    fourth: 'fjärde',
    fourth_simple: 'fjärde',
    fifth: 'femte',
    fifth_simple: 'femte',
    last: 'sista',
    last_simple: 'sista',
  },
  [Locales.NbNO]: {
    every: '(?:hver|hvert)',
    every_simple: 'hver',
    on: 'den',
    on_simple: 'den',
    the: 'den',
    the_simple: '',
    in: 'i',
    in_simple: 'i',
    and: 'og',
    days: 'dager',
    weeks: 'uker',
    months: 'måneder',
    years: 'år',
    day: 'dag',
    week: 'uke',
    month: 'måned',
    year: 'år',
    weekday: '(?:hverdager|hverdag)',
    weekday_simple: 'hverdag',
    weekend: 'helg',
    weekend_simple: 'helg',
    daily: 'daglig',
    weekly: 'ukentlig',
    monthly: 'månedlig',
    yearly: 'årlig',
    ordinals: '(?:\\.)',
    first: 'første',
    first_simple: 'første',
    second: 'andre',
    second_simple: 'andre',
    third: 'tredje',
    third_simple: 'tredje',
    fourth: 'fjerde',
    fourth_simple: 'fjerde',
    fifth: 'femte',
    fifth_simple: 'femte',
    last: 'siste',
    last_simple: 'siste',
  },
  [Locales.ItIT]: {
    every: '(?:ogni|tutti i|tutte le)',
    every_simple: 'ogni',
    on: '(?:il|la|lo)',
    on_simple: 'il',
    the: '(?:il|la|lo)',
    the_simple: '',
    in: 'di',
    in_simple: 'di',
    and: 'e',
    days: 'giorni',
    weeks: 'settimane',
    months: 'mesi',
    years: 'anni',
    day: 'giorno',
    week: 'settimana',
    month: 'mese',
    year: 'anno',
    weekday: '(?:giorni feriali|giorno feriale)',
    weekday_simple: 'giorno feriale',
    weekend: '(?:weekend|fine settimana)',
    weekend_simple: 'weekend',
    daily: 'giornalmente',
    weekly: 'settimanalmente',
    monthly: 'mensilmente',
    yearly: 'annualmente',
    ordinals: '(?:º|ª|°)',
    first: '(?:primo|prima)',
    first_simple: 'primo',
    second: '(?:secondo|seconda)',
    second_simple: 'secondo',
    third: '(?:terzo|terza)',
    third_simple: 'terzo',
    fourth: '(?:quarto|quarta)',
    fourth_simple: 'quarto',
    fifth: '(?:quinto|quinta)',
    fifth_simple: 'quinto',
    last: '(?:ultimo|ultima)',
    last_simple: 'ultimo',
  },
  [Locales.FrFR]: {
    every: '(?:chaque|tous les|toutes les)',
    every_simple: 'chaque',
    on: 'le',
    on_simple: 'le',
    the: '(?:le|la|les)',
    the_simple: '',
    in: 'de',
    in_simple: 'de',
    and: 'et',
    days: 'jours',
    weeks: 'semaines',
    months: 'mois',
    years: 'ans',
    day: 'jour',
    week: 'semaine',
    month: 'mois',
    year: 'an',
    weekday: '(?:jours ouvrés|jour ouvré|jour ouvre)',
    weekday_simple: 'jour ouvré',
    weekend: '(?:week-end|weekend)',
    weekend_simple: 'week-end',
    daily: 'quotidiennement',
    weekly: 'hebdomadairement',
    monthly: 'mensuellement',
    yearly: 'annuellement',
    ordinals: '(?:ère|ere|ème|eme|er|e)',
    first: '(?:premier|première|premiere)',
    first_simple: 'premier',
    second: '(?:deuxième|deuxieme|second)',
    second_simple: 'deuxième',
    third: '(?:troisième|troisieme)',
    third_simple: 'troisième',
    fourth: '(?:quatrième|quatrieme)',
    fourth_simple: 'quatrième',
    fifth: '(?:cinquième|cinquieme)',
    fifth_simple: 'cinquième',
    last: '(?:dernier|dernière|derniere)',
    last_simple: 'dernier',
  },
  [Locales.PtPT]: portugueseRruleKeywords,
  [Locales.PtBR]: portugueseRruleKeywords,
};
